import {
  Inter_400Regular,
  Inter_500Medium,
  Inter_600SemiBold
} from "@expo-google-fonts/inter";
import {
  PlusJakartaSans_600SemiBold,
  PlusJakartaSans_700Bold
} from "@expo-google-fonts/plus-jakarta-sans";
import { useFonts } from "expo-font";
import { DarkTheme, ThemeProvider } from "@react-navigation/native";
import * as Application from "expo-application";
import Constants from "expo-constants";
import { Stack, useNavigationContainerRef } from "expo-router";
import * as Sentry from "@sentry/react-native";
import * as SplashScreen from "expo-splash-screen";
import { StatusBar } from "expo-status-bar";
import { useEffect, useState } from "react";
import { Platform, View } from "react-native";
import { KeyboardProvider } from "react-native-keyboard-controller";

import "@/design/global.css";
import "@/design/navigationMotion";

import { useAuthState } from "@/auth/AuthProvider";
import { useRequireAuth } from "@/auth/useRequireAuth";
import { BrandCurtain } from "@/components/BrandCurtain";
import { PageBackdrop, PageBackdropProvider } from "@/components/PageBackdrop";
import {
  recordWebNavigationIntent,
  releaseWebNavigationFocus
} from "@/design/navigationMotionState";
import { lockAppPortrait } from "@/design/screenOrientation";
import { palette } from "@/design/tokens";
import { AppProviders } from "@/providers/AppProviders";
import { useAppComposed } from "@/providers/ReadinessGate";

const appVersion =
  Application.nativeApplicationVersion ?? Constants.expoConfig?.version ?? "unknown";

Sentry.init({
  dsn: "https://e08daa9df0101ceeda09c3971b37dfb2@o4511757073252352.ingest.us.sentry.io/4511757102284800",
  enabled: !__DEV__,
  environment: "production",
  release:
    process.env.EXPO_PUBLIC_SENTRY_RELEASE ??
    `${Application.applicationId ?? "com.recitalmaster.viewer"}@${appVersion}`,
  dist:
    process.env.EXPO_PUBLIC_SENTRY_DIST ??
    Application.nativeBuildVersion ??
    Platform.OS,
  sendDefaultPii: false,
  tracesSampleRate: 0,
  profilesSampleRate: 0,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,
  enableLogs: false
});

const dismissedCurtainSessions = new Set<string>();
const appNavigationTheme = {
  ...DarkTheme,
  colors: {
    ...DarkTheme.colors,
    background: "transparent",
    card: "transparent"
  }
};

function curtainSessionKey(
  authSessionKey: string | null,
  signedIn: boolean,
  demoMode: boolean
): string | null {
  if (authSessionKey) return authSessionKey;
  if (demoMode) return "demo-session";
  return signedIn ? "local-design-session" : null;
}

function initialCurtainState(sessionKey: string | null) {
  return {
    dismissed: sessionKey ? dismissedCurtainSessions.has(sessionKey) : false,
    sessionKey
  };
}

function useWebNavigationIntent() {
  const navigationRef = useNavigationContainerRef();

  useEffect(() => {
    if (Platform.OS !== "web") return;
    return navigationRef.addListener("__unsafe_action__", (event) => {
      if (!event.data.noop) {
        releaseWebNavigationFocus();
        recordWebNavigationIntent(event.data.action.type);
      }
    });
  }, [navigationRef]);
}

/**
 * The app's readiness gate. The route stack always mounts, so screens lay
 * out and their artwork decodes UNDER the curtain; the curtain lifts only
 * when the app readiness registry opens. Signed-out visitors go straight to
 * the login screen unveiled.
 */
function GuardedStack() {
  const {
    authBootstrapComplete,
    authSessionKey,
    demoMode,
    loading,
    signedIn,
    user
  } = useAuthState();
  const appComposed = useAppComposed();
  const sessionKey = curtainSessionKey(authSessionKey, signedIn, demoMode);
  const [curtainState, setCurtainState] = useState(() =>
    initialCurtainState(sessionKey)
  );
  useWebNavigationIntent();
  useRequireAuth();

  // Derive synchronously when auth changes so a newly authenticated shell is
  // covered in the very same render; an effect would be one paint too late.
  const curtainDismissed = curtainState.sessionKey === sessionKey
    ? curtainState.dismissed
    : sessionKey
      ? dismissedCurtainSessions.has(sessionKey)
      : false;
  // Firebase users must finish claim/device bootstrap. Local design-data
  // sessions have no Firebase user by definition, so requiring that bootstrap
  // would leave their curtain closed forever.
  const sessionBootstrapReady = user ? authBootstrapComplete : signedIn;
  const authenticatedSessionReady =
    sessionKey != null && sessionBootstrapReady && appComposed;
  const coldStartPending = loading || (sessionKey != null && !authenticatedSessionReady);

  useEffect(() => {
    if (curtainState.sessionKey !== sessionKey) {
      setCurtainState(initialCurtainState(sessionKey));
      return;
    }
    // A signed-out login screen is not a composed authenticated session and
    // must never consume the one reveal intended for the recital experience.
    if (!authenticatedSessionReady || curtainDismissed || !sessionKey) return;
    dismissedCurtainSessions.add(sessionKey);
    setCurtainState({ dismissed: true, sessionKey });
  }, [authenticatedSessionReady, curtainDismissed, curtainState.sessionKey, sessionKey]);

  // One reveal per authenticated session in this running document. SPA
  // history cannot re-arm it, but signing into a NEW session can. A null
  // sessionKey after any reveal is provider re-initialization churn (browser
  // history traversals remount the provider tree), never a fresh session —
  // arming on it flashed the curtain between pages on Back/Forward.
  const veiled =
    !curtainDismissed &&
    coldStartPending &&
    (sessionKey != null || dismissedCurtainSessions.size === 0);

  return (
    <PageBackdropProvider>
      <View
        style={{
          backgroundColor: Platform.OS === "web" ? "transparent" : palette.bg,
          flex: 1
        }}
      >
        <PageBackdrop ownsReadiness />
        <ThemeProvider value={appNavigationTheme}>
          <Stack
            screenOptions={{
              headerShown: false,
              contentStyle: { backgroundColor: "transparent" },
              orientation: "portrait",
              statusBarStyle: "light"
            }}
          >
            <Stack.Screen name="index" options={{ animation: "none" }} />
            <Stack.Screen name="login" options={{ animation: "none" }} />
            <Stack.Screen name="auth" options={{ animation: "none" }} />
            <Stack.Screen name="recitals" options={{ animation: "none" }} />
          </Stack>
        </ThemeProvider>
        <BrandCurtain nativeModal revealMark visible={veiled} />
        <StatusBar style="light" />
      </View>
    </PageBackdropProvider>
  );
}

// iOS can crossfade its OS-owned launch surface. Android's launch surface is
// removed atomically, so BrandCurtain independently reveals the brand lockup
// over the same opaque background on both platforms.
SplashScreen.setOptions({ duration: 300, fade: true });
SplashScreen.preventAutoHideAsync().catch(() => {});

function RootLayout() {
  const [fontsLoaded] = useFonts({
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    PlusJakartaSans_600SemiBold,
    PlusJakartaSans_700Bold
  });

  useEffect(() => {
    void lockAppPortrait();
  }, []);

  useEffect(() => {
    if (fontsLoaded) SplashScreen.hideAsync().catch(() => {});
  }, [fontsLoaded]);

  return (
    <KeyboardProvider preload={false}>
      <AppProviders>
        {fontsLoaded ? (
          <GuardedStack />
        ) : (
          <View style={{ backgroundColor: palette.bg, flex: 1 }} />
        )}
      </AppProviders>
    </KeyboardProvider>
  );
}

export default Sentry.wrap(RootLayout);
